import React, { useState } from "react";
import toast from "../../../utils/toast";
import addPhoto from "../../../Assets/Icons/imgIcon.svg";
import ImgDropzone from "../ImgDropzone";
import closeIcon from "../../../Assets/Icons/closeIcon.svg";
import AddPhoto from "./AddPhoto";
import { uploadFile } from "../../../utils/s3";

const UploadDocuments = ({
  previewImages,
  setFieldValue,
  errors,
  touched,
  s3Urls,
  setS3Urls,
  logoFileImage1,
  setLogoFileImage1,
  logoFileImage2,
  setLogoFileImage2,
  logoFileImage3,
  setLogoFileImage3,
  logoFileImage4,
  setLogoFileImage4,
  offerId,
  onAnyLoadingChange, // optional callback to bubble aggregate loading up
}) => {
  // Removed unused base64 holders to avoid unnecessary memory usage
  const [logoFileName1, setLogoFileName1] = useState("");
  const [logoFileName2, setLogoFileName2] = useState("");
  const [logoFileName3, setLogoFileName3] = useState("");
  const [logoFileName4, setLogoFileName4] = useState("");
  const [loadingImg1, setLoadingImg1] = useState(false);
  const [loadingImg2, setLoadingImg2] = useState(false);
  const [loadingImg3, setLoadingImg3] = useState(false);
  const [loadingImg4, setLoadingImg4] = useState(false);

  const getBase64 = (file, cb) => {
    let reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = function () {
      cb(reader.result);
    };
    reader.onerror = function (error) {
      console.log("Error: ", error);
    };
  };

  const validateImage = (data) => {
    var idxDot = data.lastIndexOf(".") + 1;
    var extFile = data.substr(idxDot, data.length).toLowerCase();
    if (extFile === "jpg" || extFile === "jpeg" || extFile === "png") {
      return true;
    } else {
      return false;
    }
  };

  // Compress image to stay under threshold using canvas; converts PNG to JPEG for better results
  const compressImageIfNeeded = (file, thresholdBytes = 2097152) => {
    return new Promise((resolve) => {
      if (file.size <= thresholdBytes) {
        resolve({ file, fileName: file.name });
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = async () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          const maxWidth = 1920;
          const maxHeight = 1920;
          let { width, height } = img;
          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);

          const targetMime = "image/jpeg";
          let quality = 0.85;
          const toBlobWithQuality = (q) =>
            new Promise((res) => canvas.toBlob((b) => res(b), targetMime, q));
          let blob = await toBlobWithQuality(quality);
          while (blob && blob.size > thresholdBytes && quality > 0.5) {
            quality -= 0.1;
            blob = await toBlobWithQuality(quality);
          }
          const newName = file.name.replace(/\.(png|jpeg|jpg)$/i, ".jpg");
          const compressedFile = new File([blob], newName, { type: targetMime });
          resolve({ file: compressedFile, fileName: newName });
        };
        img.onerror = () => resolve({ file, fileName: file.name });
        img.src = reader.result;
      };
      reader.onerror = () => resolve({ file, fileName: file.name });
      reader.readAsDataURL(file);
    });
  };

  const getAvailableSlots = () => {
    const slots = [];
    if (!logoFileImage1) slots.push(1);
    if (!logoFileImage2) slots.push(2);
    if (!logoFileImage3) slots.push(3);
    if (!logoFileImage4) slots.push(4);
    return slots;
  };

  const processMultipleFiles = async (files) => {
    const availableSlots = getAvailableSlots();
    const validFiles = Array.from(files).filter(file => validateImage(file.name));

    if (validFiles.length === 0) {
      toast.error("Only JPG, JPEG and PNG files are allowed!");
      return;
    }

    if (validFiles.length > availableSlots.length) {
      toast.warning(`Only ${availableSlots.length} slot(s) available. First ${availableSlots.length} valid images will be used.`);
    }

    const filesToProcess = validFiles.slice(0, availableSlots.length);
    const newPreviewImages = [...previewImages];
    const newS3Urls = [...s3Urls];

    const tasks = filesToProcess.map(async (file, i) => {
      const slotNumber = availableSlots[i];
      // Set loading state for this slot
      switch (slotNumber) {
        case 1: setLoadingImg1(true); break;
        case 2: setLoadingImg2(true); break;
        case 3: setLoadingImg3(true); break;
        case 4: setLoadingImg4(true); break;
        default: break;
      }

      const { file: fileToUpload } = await compressImageIfNeeded(file);
      const extension = fileToUpload.name.slice(fileToUpload.name.lastIndexOf(".")).toLowerCase();
      const formattedFileName = `${offerId}-previewImg${slotNumber}${extension}`;

      const logoImageUrl = URL.createObjectURL(fileToUpload);
      // Update preview instantly
      switch (slotNumber) {
        case 1: setLogoFileImage1(logoImageUrl); break;
        case 2: setLogoFileImage2(logoImageUrl); break;
        case 3: setLogoFileImage3(logoImageUrl); break;
        case 4: setLogoFileImage4(logoImageUrl); break;
        default: break;
      }

      const s3Url = await uploadFile(fileToUpload, formattedFileName);
      if (s3Url && !newS3Urls.includes(s3Url)) newS3Urls.push(s3Url);

      const existingIndex = newPreviewImages.findIndex((name) =>
        name.includes(`previewImg${slotNumber}`)
      );
      if (existingIndex !== -1) newPreviewImages[existingIndex] = formattedFileName;
      else newPreviewImages.push(formattedFileName);

      // Clear loading for this slot
      switch (slotNumber) {
        case 1: setLoadingImg1(false); break;
        case 2: setLoadingImg2(false); break;
        case 3: setLoadingImg3(false); break;
        case 4: setLoadingImg4(false); break;
        default: break;
      }
    });

    await Promise.all(tasks);

    const sortedPreviewImages = newPreviewImages.sort((a, b) => {
      const numA = parseInt(a.match(/previewImg(\d+)/)?.[1] || 0);
      const numB = parseInt(b.match(/previewImg(\d+)/)?.[1] || 0);
      return numA - numB;
    });

    setFieldValue("previewImages", sortedPreviewImages);
    setS3Urls(newS3Urls);
  };

  // Original single file handlers (kept for backward compatibility)
  // You should update these as well to consistently use the formatted file name
  // for setFieldValue("previewImages") and setLogoFileNameX.
  const img1ChangeHandler = async (file) => {
    setLoadingImg1(true);
    if (typeof onAnyLoadingChange === "function") onAnyLoadingChange(true);
    // no-op variable removed
    let logoImageUrl1 = null;
    const original = file[0];
    logoImageUrl1 = URL.createObjectURL(original);
    let checkValidImage = validateImage(original.name);
    const { file: fileToUpload } = await compressImageIfNeeded(original);
    let extension = fileToUpload.name
      .slice(file[0].name.lastIndexOf("."))
      .toLowerCase();
    const formattedFileName = `${offerId}-previewImg1${extension}`;

    if (checkValidImage) {
      const s3Url = await uploadFile(fileToUpload, formattedFileName);
      if (s3Url) {
        setS3Urls((prevS3Urls) => {
          const updatedS3Urls = [...prevS3Urls, s3Url];
          return [...new Set(updatedS3Urls)]; // Ensure uniqueness
        });
        setLoadingImg1(false);
        if (typeof onAnyLoadingChange === "function") onAnyLoadingChange(false);
      }
      setLogoFileImage1(logoImageUrl1);
      setLogoFileName1(formattedFileName); // Set formatted name
      getBase64(fileToUpload, () => {});
      // Update previewImages with the formatted name for this slot
      const updatedPreviewImages = previewImages.filter(name => !name.includes('previewImg1'));
      setFieldValue("previewImages", [...updatedPreviewImages, formattedFileName].sort((a, b) => {
        const numA = parseInt(a.match(/previewImg(\d+)/)?.[1] || 0);
        const numB = parseInt(b.match(/previewImg(\d+)/)?.[1] || 0);
        return numA - numB;
      }));
    } else {
      setLoadingImg1(false);
      if (typeof onAnyLoadingChange === "function") onAnyLoadingChange(false);
      toast.error("Only JPG, JPEG and PNG files are allowed!");
    }
  };

  const img2ChangeHandler = async (file) => {
    setLoadingImg2(true);
    if (typeof onAnyLoadingChange === "function") onAnyLoadingChange(true);
    // no-op variable removed
    let logoImageUrl2 = null;
    const original = file[0];
    logoImageUrl2 = URL.createObjectURL(original);
    let checkValidImage = validateImage(original.name);
    const { file: fileToUpload } = await compressImageIfNeeded(original);
    let extension = fileToUpload.name
      .slice(file[0].name.lastIndexOf("."))
      .toLowerCase();
    const formattedFileName = `${offerId}-previewImg2${extension}`;

    if (checkValidImage) {
      const s3Url = await uploadFile(fileToUpload, formattedFileName);
      if (s3Url) {
        setS3Urls((prevS3Urls) => {
          const updatedS3Urls = [...prevS3Urls, s3Url];
          return [...new Set(updatedS3Urls)]; // Ensure uniqueness
        });
        setLoadingImg2(false);
        if (typeof onAnyLoadingChange === "function") onAnyLoadingChange(false);
      }
      setLogoFileImage2(logoImageUrl2);
      setLogoFileName2(formattedFileName); // Set formatted name
      getBase64(fileToUpload, () => {});
      const updatedPreviewImages = previewImages.filter(name => !name.includes('previewImg2'));
      setFieldValue("previewImages", [...updatedPreviewImages, formattedFileName].sort((a, b) => {
        const numA = parseInt(a.match(/previewImg(\d+)/)?.[1] || 0);
        const numB = parseInt(b.match(/previewImg(\d+)/)?.[1] || 0);
        return numA - numB;
      }));
    } else {
      setLoadingImg2(false);
      if (typeof onAnyLoadingChange === "function") onAnyLoadingChange(false);
      toast.error("Only JPG, JPEG and PNG files are allowed!");
    }
  };

  const img3ChangeHandler = async (file) => {
    setLoadingImg3(true);
    if (typeof onAnyLoadingChange === "function") onAnyLoadingChange(true);
    // no-op variable removed
    let logoImageUrl3 = null;
    const original = file[0];
    logoImageUrl3 = URL.createObjectURL(original);
    let checkValidImage = validateImage(original.name);
    const { file: fileToUpload } = await compressImageIfNeeded(original);
    let extension = fileToUpload.name
      .slice(file[0].name.lastIndexOf("."))
      .toLowerCase();
    const formattedFileName = `${offerId}-previewImg3${extension}`;

    if (checkValidImage) {
      const s3Url = await uploadFile(fileToUpload, formattedFileName);
      if (s3Url) {
        setS3Urls((prevS3Urls) => {
          const updatedS3Urls = [...prevS3Urls, s3Url];
          return [...new Set(updatedS3Urls)]; // Ensure uniqueness
        });
        setLoadingImg3(false);
        if (typeof onAnyLoadingChange === "function") onAnyLoadingChange(false);
      }
      setLogoFileImage3(logoImageUrl3);
      setLogoFileName3(formattedFileName); // Set formatted name
      getBase64(fileToUpload, () => {});
      const updatedPreviewImages = previewImages.filter(name => !name.includes('previewImg3'));
      setFieldValue("previewImages", [...updatedPreviewImages, formattedFileName].sort((a, b) => {
        const numA = parseInt(a.match(/previewImg(\d+)/)?.[1] || 0);
        const numB = parseInt(b.match(/previewImg(\d+)/)?.[1] || 0);
        return numA - numB;
      }));
    } else {
      setLoadingImg3(false);
      if (typeof onAnyLoadingChange === "function") onAnyLoadingChange(false);
      toast.error("Only JPG, JPEG and PNG files are allowed!");
    }
  };

  const img4ChangeHandler = async (file) => {
    setLoadingImg4(true);
    if (typeof onAnyLoadingChange === "function") onAnyLoadingChange(true);
    // no-op variable removed
    let logoImageUrl4 = null;
    const original = file[0];
    logoImageUrl4 = URL.createObjectURL(original);
    let checkValidImage = validateImage(original.name);
    const { file: fileToUpload } = await compressImageIfNeeded(original);
    let extension = fileToUpload.name
      .slice(file[0].name.lastIndexOf("."))
      .toLowerCase();
    const formattedFileName = `${offerId}-previewImg4${extension}`;

    if (checkValidImage) {
      const s3Url = await uploadFile(fileToUpload, formattedFileName);
      if (s3Url) {
        setS3Urls((prevS3Urls) => {
          const updatedS3Urls = [...prevS3Urls, s3Url];
          return [...new Set(updatedS3Urls)]; // Ensure uniqueness
        });
        setLoadingImg4(false);
        if (typeof onAnyLoadingChange === "function") onAnyLoadingChange(false);
      }
      setLogoFileImage4(logoImageUrl4);
      setLogoFileName4(formattedFileName); // Set formatted name
      getBase64(fileToUpload, () => {});
      const updatedPreviewImages = previewImages.filter(name => !name.includes('previewImg4'));
      setFieldValue("previewImages", [...updatedPreviewImages, formattedFileName].sort((a, b) => {
        const numA = parseInt(a.match(/previewImg(\d+)/)?.[1] || 0);
        const numB = parseInt(b.match(/previewImg(\d+)/)?.[1] || 0);
        return numA - numB;
      }));
    } else {
      setLoadingImg4(false);
      if (typeof onAnyLoadingChange === "function") onAnyLoadingChange(false);
      toast.error("Only JPG, JPEG and PNG files are allowed!");
    }
  };

  const rejectedProofFilesHandler = (rejectedFiles) => {
    if (rejectedFiles?.length > 0) {
      rejectedFiles?.forEach((rejectedFile, index) => {
        rejectedFile.errors?.forEach((error) => {
          toast.error(error.code + " - " + error.message);
        });
      });
    }
  };

  const clearLogoImage = (slotNumber) => {
    let fileNameToClear = '';
    // removed base64 setters
    let setLogoFileImage = null;
    let setLogoFileName = null;

    switch (slotNumber) {
      case 1:
        fileNameToClear = logoFileName1;
        setLogoFileImage = setLogoFileImage1;
        setLogoFileName = setLogoFileName1;
        break;
      case 2:
        fileNameToClear = logoFileName2;
        setLogoFileImage = setLogoFileImage2;
        setLogoFileName = setLogoFileName2;
        break;
      case 3:
        fileNameToClear = logoFileName3;
        setLogoFileImage = setLogoFileImage3;
        setLogoFileName = setLogoFileName3;
        break;
      case 4:
        fileNameToClear = logoFileName4;
        setLogoFileImage = setLogoFileImage4;
        setLogoFileName = setLogoFileName4;
        break;
      default:
        return;
    }

    const updatedPreviewImages = previewImages?.filter((p) => p !== fileNameToClear);
    setFieldValue("previewImages", updatedPreviewImages);
    setLogoFileImage(null);
    setLogoFileName(""); // Clear the filename state as well

    if (s3Urls.length > 0) {
      const updatedS3Urls = s3Urls.filter(
        (url) => !url.includes(`previewImg${slotNumber}`)
      );
      setS3Urls(updatedS3Urls);
    }
  };

  const clearLogoImages = () => {
    setFieldValue("previewImages", []);
    setS3Urls([]);
    setLogoFileImage1(null);
    setLogoFileImage2(null);
    setLogoFileImage3(null);
    setLogoFileImage4(null);
    setLogoFileName1("");
    setLogoFileName2("");
    setLogoFileName3("");
    setLogoFileName4("");
  };

  // Handle the global drag and drop functionality
  const handleGlobalFileDrop = (files) => {
    processMultipleFiles(files);
  };

  // Handle browse button click
  const handleBrowseClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png, image/jpeg';
    input.multiple = true;
    input.onchange = (e) => {
      if (e.target.files.length > 0) {
        processMultipleFiles(e.target.files);
      }
    };
    input.click();
  };

  const closeButtonClass =
    "absolute -top-3 -right-3 h-[26px] w-[26px] cursor-pointer bg-[#D9D9D9] rounded-full shadow-lg z-10 flex items-center justify-center border border-white";
  const closeIconStyle = {
    filter:
      "invert(52%) sepia(71%) saturate(3082%) hue-rotate(167deg) brightness(100%) contrast(101%)",
  };
  const previewImageStyle = {
    objectFit: "cover",
    objectPosition: "center",
    width: "100%",
    height: "111px",
    borderRadius: "10px",
    overflow: "hidden",
  };

  return (
    <div
      className="h-[406px] w-[100%] mx-auto rounded-[20px] mt-5"
      style={{
        border: "2px dashed #00ADEF",
        boxShadow: "0px 4px 20px 10px #79797926",
      }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        if (e.dataTransfer.files.length > 0) {
          handleGlobalFileDrop(e.dataTransfer.files);
        }
      }}
    >
      <div className="h-full mt-2 flex flex-col justify-center items-center w-full">
        <div className="flex flex-col sm:gap-2 md:gap-4 w-[90%] mb-4">
          <div className="flex sm:gap-2 md:gap-4 w-full justify-center">
            {logoFileImage1 ? (
              <div className="w-[40%] relative">
                <div
                  className={closeButtonClass}
                  onClick={() => clearLogoImage(1)}
                >
                  <img
                    src={closeIcon}
                    className="h-[12px] w-[12px]"
                    style={closeIconStyle}
                    alt="Close"
                  />
                </div>
                <img
                  src={logoFileImage1}
                  className="h-[111px] rounded-[10px] w-full object-cover"
                  style={previewImageStyle}
                  alt="Preview 1"
                />
              </div>
            ) : (
              <div className="w-[40%]">
                <ImgDropzone
                  acceptedFiles={img1ChangeHandler}
                  rejectedFiles={rejectedProofFilesHandler}
                  maxLength={1}
                  supportText={""}
                  multiple={false}
                  img={
                    <AddPhoto
                      divClassName="w-full h-[111px]"
                      icon={addPhoto}
                      text={"Add Photo"}
                      index={1}
                      loading={loadingImg1}
                    />
                  }
                  accept="image/png, image/jpeg"
                />
              </div>
            )}
            {logoFileImage2 ? (
              <div className="w-[40%] relative">
                <div
                  className={closeButtonClass}
                  onClick={() => clearLogoImage(2)}
                >
                  <img
                    src={closeIcon}
                    className="h-[12px] w-[12px]"
                    style={closeIconStyle}
                    alt="Close"
                  />
                </div>
                <img
                  src={logoFileImage2}
                  className="h-[111px] rounded-[10px] w-full object-cover"
                  style={previewImageStyle}
                  alt="Preview 2"
                />
              </div>
            ) : (
              <div className="w-[40%]">
                <ImgDropzone
                  acceptedFiles={img2ChangeHandler}
                  rejectedFiles={rejectedProofFilesHandler}
                  maxLength={1}
                  supportText={""}
                  multiple={false}
                  img={
                    <AddPhoto
                      divClassName="w-full h-[111px]"
                      icon={addPhoto}
                      text={"Add Photo"}
                      index={2}
                      loading={loadingImg2}
                    />
                  }
                  accept="image/png, image/jpeg"
                />
              </div>
            )}
          </div>
          <div className="flex sm:gap-2 md:gap-4 w-full justify-center">
            {logoFileImage3 ? (
              <div className="w-[40%] relative">
                <div
                  className={closeButtonClass}
                  onClick={() => clearLogoImage(3)}
                >
                  <img
                    src={closeIcon}
                    className="h-[12px] w-[12px]"
                    style={closeIconStyle}
                    alt="Close"
                  />
                </div>
                <img
                  src={logoFileImage3}
                  className="h-[111px] rounded-[10px] w-full object-cover"
                  style={previewImageStyle}
                  alt="Preview 3"
                />
              </div>
            ) : (
              <div className="w-[40%]">
                <ImgDropzone
                  acceptedFiles={img3ChangeHandler}
                  rejectedFiles={rejectedProofFilesHandler}
                  maxLength={1}
                  supportText={""}
                  multiple={false}
                  img={
                    <AddPhoto
                      divClassName="w-full h-[111px]"
                      icon={addPhoto}
                      text={"Add Photo"}
                      index={3}
                      loading={loadingImg3}
                    />
                  }
                  accept="image/png, image/jpeg"
                />
              </div>
            )}
            {logoFileImage4 ? (
              <div className="w-[40%] relative">
                <div
                  className={closeButtonClass}
                  onClick={() => clearLogoImage(4)}
                >
                  <img
                    src={closeIcon}
                    className="h-[12px] w-[12px]"
                    style={closeIconStyle}
                    alt="Close"
                  />
                </div>
                <img
                  src={logoFileImage4}
                  className="h-[111px] rounded-[10px] w-full object-cover"
                  style={previewImageStyle}
                  alt="Preview 4"
                />
              </div>
            ) : (
              <div className="w-[40%] relative">
                <ImgDropzone
                  acceptedFiles={img4ChangeHandler}
                  rejectedFiles={rejectedProofFilesHandler}
                  maxLength={1}
                  supportText={""}
                  multiple={false}
                  img={
                    <AddPhoto
                      divClassName="w-full h-[111px]"
                      icon={addPhoto}
                      text={"Add Photo"}
                      index={4}
                      loading={loadingImg4}
                    />
                  }
                  accept="image/png, image/jpeg"
                />
              </div>
            )}
          </div>
        </div>
        {!logoFileImage1 &&
          !logoFileImage2 &&
          !logoFileImage3 &&
          !logoFileImage4 && (
            <>
              <span className="sm:text-[16px] md:text-[20px] font-[400] text-[#8C8C8C] mb-1 font-nunito">
                Drag & Drop
              </span>
              <span className="sm:text-[16px] md:text-[20px] font-[400] text-[#8C8C8C] font-nunito">
                Your files here Or{" "}
                <span
                  className="text-[#00ADEF] cursor-pointer"
                  onClick={handleBrowseClick}
                >
                  Browse
                </span> to upload
              </span>
            </>
          )}
        {(logoFileImage1 ||
          logoFileImage2 ||
          logoFileImage3 ||
          logoFileImage4) && (
            <button
              type="button"
              className="my-5 shadow-none sm:w-[30%] h-[40px] rounded-[5px] bg-[#00ADEF] text-[16px] font-[500] text-[#FFFFFF]"
              onClick={clearLogoImages}
            >
              Clear
            </button>
          )}
      </div>
      <div className="ms-1">
        <span className="text-red-600 sm:text-[10px] md:text-[12px] font-[600] text-start">
          {errors?.previewImages && touched?.previewImages
            ? errors?.previewImages
            : null}
        </span>
      </div>
    </div>
  );
};

export default UploadDocuments;